import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCatalogue } from '../context/CatalogueContext';
import { searchProducts, SEARCH_HINTS } from '../lib/searchProducts';
import { bottleImg } from '../mock';

export default function SearchDialog({ open, onClose }) {
  const { products } = useCatalogue();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const results = useMemo(() => searchProducts(products, query), [products, query]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setActive(0);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const go = (product) => {
    if (!product) return;
    onClose();
    navigate(`/products/${product.slug}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(results.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (results[active]) go(results[active]);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120]" role="dialog" aria-modal="true" aria-label="Search fragrances">
      <button type="button" className="absolute inset-0 bg-[#1A2332]/55" aria-label="Close search" onClick={onClose} />
      <div className="relative mx-auto mt-[max(4rem,12vh)] w-[min(36rem,calc(100%-1.5rem))] bg-[#F5EFE6] border border-[#E5DCC9] shadow-[0_24px_80px_rgba(26,35,50,0.28)]">
        <div className="flex items-center gap-2 px-4 border-b border-[#E5DCC9]">
          <Search className="w-4 h-4 text-[#1A2332]/50 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search by scent, note, mood…"
            className="flex-1 bg-transparent py-4 text-sm text-[#1A2332] outline-none placeholder:text-[#1A2332]/40"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" onClick={onClose} className="p-2 -mr-1 text-[#1A2332]/60" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!query.trim() ? (
          <div className="p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/45 mb-3">Try searching</p>
            <div className="flex flex-wrap gap-2">
              {SEARCH_HINTS.map((hint) => (
                <button
                  key={hint}
                  type="button"
                  onClick={() => setQuery(hint)}
                  className="px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase border border-[#E5DCC9] text-[#1A2332]/80 hover:border-[#C9A961]"
                >
                  {hint}
                </button>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-[#1A2332]/45">
              Understands notes, occasions, and names — oud, office, for him, rose.
            </p>
          </div>
        ) : results.length === 0 ? (
          <p className="px-4 py-8 text-sm text-[#1A2332]/55 text-center">
            No match for “{query}”. Try a note, a mood, or a bottle name.
          </p>
        ) : (
          <ul className="max-h-[min(24rem,55vh)] overflow-y-auto py-2">
            {results.map((product, i) => (
              <li key={product.id}>
                <button
                  type="button"
                  onClick={() => go(product)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                    i === active ? 'bg-white/80' : ''
                  }`}
                >
                  <img
                    src={(product.shots && product.shots[0]) || bottleImg(product.slug)}
                    alt=""
                    className="w-10 h-12 object-cover flex-shrink-0 bg-[#F0E6D3]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-serif-display text-base text-[#1A2332] truncate">{product.name}</span>
                    <span className="block text-[11px] text-[#1A2332]/55 truncate">
                      {product.gender} · {product.family} · {product.tagline}
                    </span>
                  </span>
                  <span className="font-serif-display text-sm text-[#1A2332]">₹{product.price}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
}
